import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router-dom";
import IPosModal from "../../IPosModal";
import { getLanguageById } from "../../helpers/language";
import { convertDotNetTimeToMomentDate } from "../../helpers/dateTime";
import {
  setLanguageId,
  setUiLanguageId,
} from "../../redux/actions/baseActions";
import "./HeaderMenu.scss";

function useOutsideAlerter(ref, action) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        action();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
}

function Separator({ color = "white", height = 1 }) {
  return (
    <hr className="separator-line"
      style={{
        backgroundColor: color,
        height: height,
        border: "none"
      }}
    />
  );
}

const HeaderMenu = ({
  t,
  setLanguageId,
  branchInfo,
  setUiLanguageId,
  currentLanguageId,  
}) => {
  const [workingHoursVisible, setWorkingHoursVisible] = useState(false);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const wrapperRef = useRef(null);
  const history = useHistory();

  useOutsideAlerter(wrapperRef, function () {
    const checkbox = document.getElementById("close-mobile-menu");
    if (checkbox.value === "true") {
      checkbox.click();
    }
  });

  const branchWazeLink =
    branchInfo.latitude && branchInfo.longitude
      ? `https://waze.com/ul?ll=${branchInfo.latitude},${branchInfo.longitude}&=10`
      : `https://waze.com/ul?q=${branchInfo.Address}&navigate=yes&utm_campaign=default&utm_source=waze_website&utm_medium=lm_share_location`;

  const changeLanguageId = (id) => {
    setUiLanguageId(id);
    setLanguageId(id === 3 ? 1 : id);
  };

  const goToHome = () => {
    IPosModal.confirmation({
      title: "", //t('dialogs.redirect_to_home_title'),
      message: t("dialogs.redirect_to_home_description"),
      okButtonText: t("buttons.ok"),
      cancelButtonText: t("buttons.cancel"),
      onOk: () => {
        const lang = getLanguageById(currentLanguageId);
        if (branchInfo && branchInfo.branch_guid_code) {
          history.push(`/${branchInfo.parent_chain_guid_code ? branchInfo.parent_chain_guid_code : branchInfo.branch_guid_code}/type?lang=${lang}`);
        } else {
          history.push("/");
        }

        window.location.reload();
      },
      onClose: () => {
        return;
      },
    });
  };

  const goToAccessibility = () => {
    window.open('https://www.ipos.co.il/accessibility', '_blank');
  };

  const goRestSite = () => {
    window.open(branchInfo.site_url_txt, '_blank');
  };

  const goToPrivacy = () => {
    window.open('https://www.ipos.co.il/privacy-terms-service', '_blank');
  };

  return (
    <div
      className={`header-menu ${menuOpened ? "opened" : "closed"}`}
      ref={wrapperRef}
    >
      <input
        type="checkbox"
        id={"close-mobile-menu"}
        className={"hidden-menu-checkbox"}
        value={menuOpened}
        onClick={() => {
          setMenuOpened(!menuOpened);
        }}
      />
      <span className="icon-bar" key={1} />
      <span className="icon-bar" key={2} />
      <span className="icon-bar" key={3} />

      <div id="menu">
        <div className={"branch-info"}>
          <div className={"place-name"}>{branchInfo.PlaseName}</div>
        </div>
        <Separator />
        {branchInfo.site_url_txt && (
            <div className="branch-info restart-order">
              <button
                className={"btn btn-primary btn-restart-order"}
                onClick={goRestSite}
              >
                <FontAwesomeIcon icon="home" />
                {t("branch_info.rest_site_url")}
              </button>
            </div>                   
          )}

        {branchInfo.site_url_txt && (
          <Separator />
        )}

        <div className={"menu-content"}>
          <div className={"branch-info"}>
            <FontAwesomeIcon icon={["fab", "waze"]} />
            {branchInfo.use_pin_pad_flg === 1 && <b> {branchInfo.Address}</b>}

            {(branchInfo.use_pin_pad_flg === 0 ||
              !branchInfo.use_pin_pad_flg) && (
              <a target={"blank"} href={branchWazeLink}>
                {branchInfo.Address}
              </a>
            )}
          </div>
          <Separator />          

          <div className="branch-info">
            <FontAwesomeIcon icon="phone" />
            {branchInfo.use_pin_pad_flg === 1 && <b> {branchInfo.Phone}</b>}

            {(branchInfo.use_pin_pad_flg === 0 ||
              !branchInfo.use_pin_pad_flg) && (
              <a href={`tel:${branchInfo.Phone}`}>{branchInfo.Phone}</a>
            )}
          </div>
          <Separator />
          <div
            className="branch-info"
            onClick={() => {
              setAboutVisible(!aboutVisible);
            }}
          >
            <div className={"arrow-down"}>
              {!aboutVisible && <FontAwesomeIcon icon={"chevron-down"} />}
              {aboutVisible && <FontAwesomeIcon icon={"chevron-up"} />}
            </div>
            <FontAwesomeIcon icon="info-circle" />
            {t("branch_info.about")}
          </div>
          {!aboutVisible && <Separator />}
          {aboutVisible && (
            <div
              className={"about-block"}
              dangerouslySetInnerHTML={{
                __html: branchInfo.Details.replace(/(?:\r\n|\r|\n)/g, "<br />"),
              }}
            ></div>
          )}
          {aboutVisible && <Separator />}
          <div
            className="branch-info"
            onClick={() => {
              setWorkingHoursVisible(!workingHoursVisible);
            }}
          >
            <div className={"arrow-down"}>
              {!workingHoursVisible && (
                <FontAwesomeIcon icon={"chevron-down"} />
              )}
              {workingHoursVisible && <FontAwesomeIcon icon={"chevron-up"} />}
            </div>
            <FontAwesomeIcon icon="clock" />
            {t("working_time.working_hours")}
          </div>
          {!workingHoursVisible && <Separator />}
          {workingHoursVisible && (
            <div className={"working-hours-block"}>
              <ul className="working-hours-list">
                {branchInfo.DeliveryTimes &&
                  branchInfo.DeliveryTimes.map((deliveryTime) => (
                    <li key={deliveryTime.delivery_time_id}>
                      <i className="fa fa-clock-o" />
                      <span className="clock">
                        {moment().day(deliveryTime.day_of_week).format("dddd")}
                      </span>
                      <span className="bold clock">
                        {!deliveryTime.start_dt && !deliveryTime.end_dt
                          ? t("working_time.closed")
                          : `${convertDotNetTimeToMomentDate(
                              deliveryTime.start_dt
                            ).format("HH:mm")}
                      -
                         ${convertDotNetTimeToMomentDate(
                           deliveryTime.end_dt
                         ).format("HH:mm")}`}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {workingHoursVisible && <Separator />}

          <div className="branch-info restart-order">
            <button
              className={"btn btn-primary btn-restart-order"}
              onClick={goToHome}
            >
              <FontAwesomeIcon icon="redo" />
              {t("buttons.restart_order")}
            </button>
          </div>
          <Separator />
          <div className="branch-info restart-order">
            <button
              className={"btn btn-primary btn-restart-order"}
              onClick={goToAccessibility}
            >
              <FontAwesomeIcon icon="newspaper" />
              {t("buttons.accessibility")}
            </button>
          </div>
          <Separator />
          <div className="branch-info restart-order">
            <button
              className={"btn btn-primary btn-restart-order"}
              onClick={goToPrivacy}
            >
              <FontAwesomeIcon icon="newspaper" />
              {t("buttons.privacy")}
            </button>
          </div>
          <Separator />
          <div className="branch-info restart-order">
            {"Ver: 1.3.38"}
          </div>
        </div>
        <Separator />

        <div className={"menu-footer"}>
          <div className="ipos-copyright">
            powered by
            {branchInfo.use_pin_pad_flg === 1 && <b> iPOS</b>}
            {(branchInfo.use_pin_pad_flg === 0 ||
              !branchInfo.use_pin_pad_flg) && (
              <a href={"http://www.ipos.co.il"} target={"blank"}>
                {" "}
                iPOS
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { 
    currentLanguageId, 
    branchInfo 
  } = state;

  return {
    currentLanguageId,
    branchInfo,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setLanguageId,
      setUiLanguageId,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(HeaderMenu);
